import { Box, Button, DialogContent, TextField, useTheme } from "@mui/material";
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import '@mui/lab';
import { AdapterDayjs as DateAdapter } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from "dayjs";
import { useEffect, useRef, useState } from "react";
import { UpsertAction } from "../Enums";
import { useErrorDisplay } from '../components/ErrorDisplay';
import BusySpinner from "../components/BusySpinner";
import { GetUpsertIcon, IsNotNullOrUndefined } from "../Helpers";
import AssessmentPeriodClient, { AssessmentPeriod } from "../apiClients/AssessmentPeriodClient";
import ConditionalDisplay from "../components/ConditionalDisplay";
import IconButtonEx, { IconButtonExProps } from "../components/IconButtonEx";
import { MobileFormStack, ResponsiveDialog, ResponsiveDialogHeader, StickyDialogActions } from "../components/responsive";

export interface AssessmentPeriodUpsertPopupProps extends Omit<IconButtonExProps, 'onClick'> {
    mode: UpsertAction;
    assessmentPeriodId?: string;
    inAssessmentPeriod?: AssessmentPeriod;
    onClose?: () => Promise<void> | void;
    icon?: JSX.Element;
}

const AssessmentPeriodUpsertPopup = ({ mode, assessmentPeriodId, inAssessmentPeriod, onClose = () => { }, icon = GetUpsertIcon(mode), ...btnProps }: AssessmentPeriodUpsertPopupProps) => {

    const newAssessmentPeriod = {
        Id: '',
        OrganizationId: '',
        StartDate: '',
        EndDate: ''
    }

    const minPeriodDate = 1;

    const [ErrorDisplay, setError] = useErrorDisplay();
    const [busy, setBusy] = useState<boolean>(false);
    const [open, setOpen] = useState<boolean>(false);
    const initialized = useRef<boolean>();
    const theme = useTheme();

    const [assessmentPeriod, setAssessmentPeriod] = useState<AssessmentPeriod>(null);
    const [dataHasChanged, setDataHasChanged] = useState<boolean>(false);


    const [localStartDate, setLocalStartDate] = useState<dayjs.Dayjs>(dayjs().add(1, 'day'));
    const [localEndDate, setLocalEndDate] = useState<dayjs.Dayjs>(dayjs().add(minPeriodDate + 1, 'day'));

    const title = 'Create Assessment Period';

    const loadData = async () => {
        try {
            setBusy(true);

            switch (mode) {
                case UpsertAction.Create:
                    setAssessmentPeriod(newAssessmentPeriod);
                    break;
                case UpsertAction.Update:
                    setDataHasChanged(false);
                    break;
                case UpsertAction.View:
                    await setAssessmentPeriod(inAssessmentPeriod);

                    setLocalStartDate(dayjs(inAssessmentPeriod?.StartDate));
                    setLocalEndDate(dayjs(inAssessmentPeriod?.EndDate));
                    break;
                default:
                    break;
            }
        }
        catch (error) {
            setError(error, "Error creating/updating Assessment Period");
        }
        finally {
            initialized.current = true;
            setBusy(false);
        }
    }

    useEffect(() => {
        if (mode === UpsertAction.View && IsNotNullOrUndefined(inAssessmentPeriod))
            setAssessmentPeriod(inAssessmentPeriod);

        if (initialized.current)
            return;

        loadData();
    }, [inAssessmentPeriod]);

    const handleSubmit = async () => {
        try {
            setBusy(true);

            if (mode === UpsertAction.Create) {
                let cap = {
                    StartDate: assessmentPeriod.StartDate,
                    EndDate: assessmentPeriod.EndDate
                }

                await new AssessmentPeriodClient().CreateAssessmentPeriod(cap);
                setDataHasChanged(false);
            }

            setOpen(false);
            onClose();

        } catch (error) {
            setError(error, "Error creating AssessmentPeriod");
        }
        finally {
            setBusy(false);
        }
    };

    const handleDateInputChange = async (name, value: dayjs.Dayjs) => {

        if (name === 'StartDate') {
            let endDate = value.add(minPeriodDate, 'day');
            await setAssessmentPeriod({ ...assessmentPeriod, EndDate: endDate.format() })
            setLocalEndDate(endDate);
            setLocalStartDate(value);
        } else {
            setLocalEndDate(value);
        }

        await setAssessmentPeriod({ ...assessmentPeriod, [name]: value.format() })

        setDataHasChanged(true);

    };

    if (!initialized.current) {
        return <></>
    } else {
        return (
            <>
                <ErrorDisplay />
                <IconButtonEx disabled={busy} hoverContent='' size='small' onClick={() => setOpen(true)} {...btnProps}>{icon}</IconButtonEx>

                <ResponsiveDialog open={open} fullWidth={false} maxWidth='lg' tabletFullScreen="never" aria-labelledby="assessment-period-upsert-title" PaperProps={{ sx: { overflow: 'hidden' } }}>
                    <ResponsiveDialogHeader id="assessment-period-upsert-title" title={title.trim()} />
                    <DialogContent dividers sx={{ overflowY: 'auto' }}>
                        <BusySpinner busy={busy}>
                            <MobileFormStack tabletColumns={2} desktopColumns={1} sx={{ border: 1 }}>
                                        <Box>
                                            <LocalizationProvider dateAdapter={DateAdapter}>
                                                <DatePicker
                                                    label="Start Date"
                                                    disabled={mode === UpsertAction.View}
                                                    value={localStartDate}
                                                    minDate={dayjs().add(1, 'day')}
                                                    onChange={(newValue) => handleDateInputChange('StartDate', newValue)}
                                                    renderInput={(params) => <TextField {...params} fullWidth />}
                                                />
                                            </LocalizationProvider>
                                        </Box>
                                        <Box>
                                            <LocalizationProvider dateAdapter={DateAdapter}>
                                                <DatePicker
                                                    label="End Date"
                                                    disabled={mode === UpsertAction.View}
                                                    value={localEndDate}
                                                    minDate={localStartDate.add(minPeriodDate, 'day')}
                                                    onChange={(newValue) => handleDateInputChange('EndDate', newValue)}
                                                    renderInput={(params) => <TextField {...params} fullWidth />}
                                                />
                                            </LocalizationProvider>
                                        </Box>
                            </MobileFormStack>
                            {/* <Grid container sx={{ flexDirection: 'row-reverse' }}>
                                <ConditionalDisplay ifTrue={mode === UpsertAction.Create || mode === UpsertAction.Update}>
                                    <Button variant='outlined' disabled={!dataHasChanged} onClick={handleSubmit}>Submit</Button>
                                </ConditionalDisplay>
                            </Grid> */}
                        </BusySpinner>
                    </DialogContent>
                    <StickyDialogActions phoneLayout="stacked" sx={{ margin: 0, padding: theme.spacing(1) }}>
                        <Button onClick={() => setOpen(false)} variant='text' color='inherit'>Cancel</Button>
                        <Button onClick={handleSubmit} variant='outlined' color='primary'>Submit</Button>
                    </StickyDialogActions>
                </ResponsiveDialog>
            </>
        )
    }
};

export default AssessmentPeriodUpsertPopup;
